import React, {FocusEvent, useLayoutEffect, useRef} from 'react';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import {UserOsType} from '@shared/cci/features/const/environment';

export interface InputTextProps {
  id?: string;
  type?: 'text' | 'password';
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  className?: string;
  placeholder?: string;
  regex?: RegExp;
  maxLength?: number;
  initValue: string | null | undefined;
  isDisabled?: boolean;
  isAutoTrim?: boolean;
  required?: boolean;
  autofucus?: boolean;

  onFocus?(e: FocusEvent): void;

  onChange(value: string): void;
}

function InputText({
  id = undefined,
  type = 'text',
  inputMode = 'text',
  className = undefined,
  placeholder = undefined,
  regex = undefined,
  maxLength = undefined,
  initValue,
  isDisabled = false,
  isAutoTrim = true,
  onFocus,
  onChange,
  required = false,
}: InputTextProps): JSX.Element {
  const os = useEnvironmentStore((state) => state.os);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const originalValue = e.target.value;
    let prevStart = e.target.selectionStart || 0;
    let prevEnd = e.target.selectionEnd || 0;

    let filteredValue = originalValue;
    if (regex) {
      filteredValue = originalValue.replace(regex, '');
      prevStart -= originalValue.length - filteredValue.length;
      prevEnd -= originalValue.length - filteredValue.length;
    }
    if (maxLength) {
      filteredValue = filteredValue.slice(0, maxLength);
    }

    onChange(filteredValue);
    e.target.value = filteredValue;
    e.target.setSelectionRange(prevStart, prevEnd);
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    if (!isAutoTrim) {
      return;
    }

    const originalValue = e.target.value;
    if (originalValue) {
      const filteredValue = originalValue.replace(/\s+$/, '');
      e.target.value = filteredValue;

      if (originalValue !== filteredValue) {
        onChange(filteredValue);
      }
    }
  }

  useLayoutEffect(() => {
    if (os !== UserOsType.IOS) {
      // 사파리는 이 동작을 브라우저가 해줘서, 이걸 임의로 하면 오히려 오동작
      if (inputRef.current) {
        // type 이 바뀌는 input 에서, (ex. password <-> text) type 교체 시에도 커서 셀렉션을 유지해줌
        const {selectionStart, selectionEnd} = inputRef.current;
        setTimeout(() => {
          inputRef.current!.setSelectionRange(selectionStart, selectionEnd);
        }, 20);
      }
    }
  }, [type]);

  return (
    <input
      type={type}
      inputMode={inputMode}
      id={id}
      ref={inputRef}
      className={className}
      placeholder={placeholder}
      value={initValue || ''}
      disabled={!!isDisabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      autoComplete="off"
      required={required}
    />
  );
}

export default InputText;
